/**
 * System Template Service
 * 
 * Manages system and admin templates in the email_templates collection.
 * 
 * Purpose:
 * - Global templates visible to ALL users (marketplace)
 * - Only system or admin can create/modify
 * - Users can view and copy, but not edit originals
 * 
 * Collection: email_templates
 * Allowed createdBy values: 'system' | 'admin'
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

// ============================================================================
// Types & Interfaces
// ============================================================================

export type TemplateCategory =
  | 'arts-entertainment'
  | 'blogs-updates'
  | 'business'
  | 'deals-offers'
  | 'ecommerce'
  | 'events'
  | 'festive-season'
  | 'health-wellness'
  | 'newsletter'
  | 'onboarding'
  | 'professional-services'
  | 'promotional';

export interface SystemTemplate {
  id: string;
  name: string;
  subject: string;
  description: string;
  htmlContent: string;
  textContent?: string;
  category: TemplateCategory;
  tags: string[];
  variables: string[];

  // Metadata
  createdBy: 'system' | 'admin'; // ONLY system or admin
  createdAt: Timestamp;
  updatedAt: Timestamp;

  // Display
  previewImage?: string;
  isActive: boolean;

  // Marketplace
  isPaid: boolean;
  price: number;
  downloadCount: number;

  // Featured/ordering
  featuredOnDiscoverPage?: boolean;
  discoverPageOrder?: number;
  displayOrder?: number;

  // Editor design (for drag-and-drop builder)
  designJson?: unknown;
}

export interface CreateSystemTemplateData {
  name: string;
  subject: string;
  description: string;
  htmlContent: string;
  textContent?: string;
  category: TemplateCategory;
  tags: string[];
  variables?: string[];
  previewImage?: string;
  isPaid?: boolean;
  price?: number;
  designJson?: unknown;
  featuredOnDiscoverPage?: boolean;
  discoverPageOrder?: number;
  displayOrder?: number;
}

export interface UpdateSystemTemplateData {
  name?: string;
  subject?: string;
  description?: string;
  htmlContent?: string;
  textContent?: string;
  category?: TemplateCategory;
  tags?: string[];
  variables?: string[];
  previewImage?: string;
  isActive?: boolean;
  isPaid?: boolean;
  price?: number;
  designJson?: unknown;
  featuredOnDiscoverPage?: boolean;
  discoverPageOrder?: number;
  displayOrder?: number;
}

export interface SystemTemplateFilters {
  category?: TemplateCategory;
  isPaid?: boolean;
  isActive?: boolean;
  tags?: string[];
  search?: string;
  featured?: boolean;
}

// ============================================================================
// System Template Service Class
// ============================================================================

class SystemTemplateService {
  private readonly collectionName = 'email_templates';

  /**
   * Get all system templates
   * 
   * @param filters - Optional filters to apply
   * @returns Array of system templates
   */
  async getSystemTemplates(filters?: SystemTemplateFilters): Promise<SystemTemplate[]> {
    try {
      const col = collection(db, this.collectionName);
      
      // Build base query - only system/admin templates
      let q = query(
        col,
        where('createdBy', 'in', ['system', 'admin'])
      );

      // Apply filters
      if (filters?.category) {
        q = query(q, where('category', '==', filters.category));
      }

      if (filters?.isPaid !== undefined) {
        q = query(q, where('isPaid', '==', filters.isPaid));
      }

      if (filters?.isActive !== undefined) {
        q = query(q, where('isActive', '==', filters.isActive));
      }

      if (filters?.featured) {
        q = query(q, where('featuredOnDiscoverPage', '==', true));
      }

      const snapshot = await getDocs(q);
      let templates: SystemTemplate[] = [];

      snapshot.forEach((doc) => {
        templates.push(this.docToTemplate(doc.id, doc.data()));
      });

      // Apply client-side filters
      if (filters?.tags && filters.tags.length > 0) {
        templates = templates.filter((t) =>
          filters.tags!.some((tag) => t.tags.includes(tag))
        );
      }

      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        templates = templates.filter(
          (t) =>
            t.name.toLowerCase().includes(searchLower) ||
            t.description.toLowerCase().includes(searchLower) ||
            t.tags.some((tag) => tag.toLowerCase().includes(searchLower))
        );
      }

      // Sort by displayOrder, then by createdAt
      templates.sort((a, b) => {
        // First sort by displayOrder
        const orderA = a.displayOrder ?? Infinity;
        const orderB = b.displayOrder ?? Infinity;
        if (orderA !== orderB) return orderA - orderB;

        // Then by createdAt (newest first)
        return b.createdAt.toMillis() - a.createdAt.toMillis();
      });

      return templates;
    } catch (error) {
      console.error('Error getting system templates:', error);
      throw new Error('Failed to get system templates');
    }
  }

  /**
   * Get active system templates only (for user-facing pages)
   * 
   * @param filters - Optional filters
   * @returns Active templates only
   */
  async getActiveTemplates(filters?: Omit<SystemTemplateFilters, 'isActive'>): Promise<SystemTemplate[]> {
    return this.getSystemTemplates({ ...filters, isActive: true });
  }

  /**
   * Get featured templates for discover/marketplace page
   * 
   * @param limit - Max number of templates to return
   * @returns Featured templates sorted by discoverPageOrder
   */
  async getFeaturedTemplates(limit: number = 10): Promise<SystemTemplate[]> {
    try {
      const templates = await this.getSystemTemplates({
        featured: true,
        isActive: true,
      });

      // Sort by discoverPageOrder
      templates.sort((a, b) => {
        const orderA = a.discoverPageOrder ?? Infinity;
        const orderB = b.discoverPageOrder ?? Infinity;
        return orderA - orderB;
      });

      return templates.slice(0, limit);
    } catch (error) {
      console.error('Error getting featured templates:', error);
      throw new Error('Failed to get featured templates');
    }
  }

  /**
   * Get a single system template by ID
   * 
   * @param templateId - Template document ID
   * @returns Template or null if not found
   */
  async getTemplateById(templateId: string): Promise<SystemTemplate | null> {
    try {
      const docRef = doc(db, this.collectionName, templateId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();

      // Verify it's a system/admin template
      if (data.createdBy !== 'system' && data.createdBy !== 'admin') {
        console.warn(`Template ${templateId} is not a system/admin template`);
        return null;
      }

      return this.docToTemplate(docSnap.id, data);
    } catch (error) {
      console.error('Error getting template by ID:', error);
      throw new Error('Failed to get template');
    }
  }

  /**
   * Create a new system template (admin only)
   * 
   * @param data - Template data
   * @param createdBy - 'system' or 'admin'
   * @returns Template ID
   */
  async createTemplate(
    data: CreateSystemTemplateData,
    createdBy: 'system' | 'admin' = 'admin'
  ): Promise<string> {
    try {
      // Validate required fields
      if (!data.name || !data.subject || !data.htmlContent) {
        throw new Error('Missing required fields: name, subject, htmlContent');
      }

      const now = Timestamp.now();

      const templateDoc = {
        name: data.name.trim(),
        subject: data.subject.trim(),
        description: data.description?.trim() || '',
        htmlContent: data.htmlContent,
        textContent: data.textContent || '',
        category: data.category,
        tags: data.tags || [],
        variables: data.variables || [],
        
        // Metadata
        createdBy,
        createdAt: now,
        updatedAt: now,
        
        // Display
        previewImage: data.previewImage || '',
        isActive: true,
        
        // Marketplace
        isPaid: data.isPaid ?? false,
        price: data.price ?? 0,
        downloadCount: 0,
        
        // Featured/ordering
        featuredOnDiscoverPage: data.featuredOnDiscoverPage ?? false,
        discoverPageOrder: data.discoverPageOrder,
        displayOrder: data.displayOrder,
        
        // Editor design
        designJson: data.designJson,
      };

      const docRef = await addDoc(collection(db, this.collectionName), templateDoc);
      
      console.log(`✅ System template created: ${docRef.id} (${data.name})`);
      return docRef.id;
    } catch (error) {
      console.error('Error creating system template:', error);
      throw new Error(`Failed to create template: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Update an existing system template (admin only)
   * 
   * @param templateId - Template document ID
   * @param updates - Fields to update
   */
  async updateTemplate(
    templateId: string,
    updates: UpdateSystemTemplateData
  ): Promise<void> {
    try {
      // Verify template exists and is system/admin
      const existing = await this.getTemplateById(templateId);
      if (!existing) {
        throw new Error('Template not found or is not a system template');
      }

      const docRef = doc(db, this.collectionName, templateId);
      
      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.now(),
      };

      await updateDoc(docRef, updateData);
      
      console.log(`✅ System template updated: ${templateId}`);
    } catch (error) {
      console.error('Error updating system template:', error);
      throw new Error(`Failed to update template: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Delete a system template (admin only)
   * 
   * WARNING: This permanently deletes the template.
   * Consider soft-delete (isActive: false) instead.
   * 
   * @param templateId - Template document ID
   */
  async deleteTemplate(templateId: string): Promise<void> {
    try {
      // Verify template exists and is system/admin
      const existing = await this.getTemplateById(templateId);
      if (!existing) {
        throw new Error('Template not found or is not a system template');
      }

      const docRef = doc(db, this.collectionName, templateId);
      await deleteDoc(docRef);
      
      console.log(`✅ System template deleted: ${templateId}`);
    } catch (error) {
      console.error('Error deleting system template:', error);
      throw new Error(`Failed to delete template: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Soft delete - deactivate template instead of deleting
   * (Recommended over hard delete)
   * 
   * @param templateId - Template document ID
   */
  async deactivateTemplate(templateId: string): Promise<void> {
    await this.updateTemplate(templateId, { isActive: false });
    console.log(`✅ System template deactivated: ${templateId}`);
  }

  /**
   * Reactivate a deactivated template
   * 
   * @param templateId - Template document ID
   */
  async activateTemplate(templateId: string): Promise<void> {
    await this.updateTemplate(templateId, { isActive: true });
    console.log(`✅ System template activated: ${templateId}`);
  }

  /**
   * Increment download count when a user copies this template
   * 
   * @param templateId - Template document ID
   */
  async incrementDownloadCount(templateId: string): Promise<void> {
    try {
      const template = await this.getTemplateById(templateId);
      if (!template) {
        throw new Error('Template not found');
      }

      const docRef = doc(db, this.collectionName, templateId);
      await updateDoc(docRef, {
        downloadCount: (template.downloadCount || 0) + 1,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error incrementing download count:', error);
      // Don't throw - this is non-critical
    }
  }

  /**
   * Search templates by query string
   * 
   * @param searchQuery - Search string
   * @param filters - Additional filters
   * @returns Matching templates
   */
  async searchTemplates(
    searchQuery: string,
    filters?: Omit<SystemTemplateFilters, 'search'>
  ): Promise<SystemTemplate[]> {
    return this.getSystemTemplates({
      ...filters,
      search: searchQuery,
    });
  }

  /**
   * Get templates by category
   * 
   * @param category - Template category
   * @param activeOnly - Only return active templates
   * @returns Templates in category
   */
  async getTemplatesByCategory(
    category: TemplateCategory,
    activeOnly: boolean = true
  ): Promise<SystemTemplate[]> {
    return this.getSystemTemplates({
      category,
      isActive: activeOnly ? true : undefined,
    });
  }

  /**
   * Get free templates only
   * 
   * @returns Free templates
   */
  async getFreeTemplates(): Promise<SystemTemplate[]> {
    return this.getSystemTemplates({
      isPaid: false,
      isActive: true,
    });
  }

  /**
   * Get paid/premium templates only
   * 
   * @returns Premium templates
   */
  async getPremiumTemplates(): Promise<SystemTemplate[]> {
    return this.getSystemTemplates({
      isPaid: true,
      isActive: true,
    });
  }

  /**
   * Batch update multiple templates
   * (e.g., setting displayOrder for all templates)
   * 
   * @param updates - Array of { id, data } objects
   */
  async batchUpdateTemplates(
    updates: Array<{ id: string; data: UpdateSystemTemplateData }>
  ): Promise<void> {
    try {
      const batch = writeBatch(db);
      const now = Timestamp.now();

      for (const update of updates) {
        const docRef = doc(db, this.collectionName, update.id);
        batch.update(docRef, {
          ...update.data,
          updatedAt: now,
        });
      }

      await batch.commit();
      console.log(`✅ Batch updated ${updates.length} templates`);
    } catch (error) {
      console.error('Error batch updating templates:', error);
      throw new Error('Failed to batch update templates');
    }
  }

  /**
   * Get template statistics
   * 
   * @returns Statistics about system templates
   */
  async getTemplateStats(): Promise<{
    total: number;
    active: number;
    inactive: number;
    byCategory: Record<string, number>;
    byCreator: Record<string, number>;
    free: number;
    paid: number;
    featured: number;
  }> {
    try {
      const allTemplates = await this.getSystemTemplates();

      const stats = {
        total: allTemplates.length,
        active: allTemplates.filter((t) => t.isActive).length,
        inactive: allTemplates.filter((t) => !t.isActive).length,
        byCategory: {} as Record<string, number>,
        byCreator: {} as Record<string, number>,
        free: allTemplates.filter((t) => !t.isPaid).length,
        paid: allTemplates.filter((t) => t.isPaid).length,
        featured: allTemplates.filter((t) => t.featuredOnDiscoverPage).length,
      };

      allTemplates.forEach((template) => {
        // Count by category
        stats.byCategory[template.category] =
          (stats.byCategory[template.category] || 0) + 1;

        // Count by creator
        stats.byCreator[template.createdBy] =
          (stats.byCreator[template.createdBy] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error getting template stats:', error);
      throw new Error('Failed to get template stats');
    }
  }

  // ============================================================================
  // Private Helper Methods
  // ============================================================================

  /**
   * Convert Firestore document to SystemTemplate object
   */
  private docToTemplate(id: string, data: any): SystemTemplate {
    return {
      id,
      name: data.name || 'Untitled',
      subject: data.subject || '',
      description: data.description || '',
      htmlContent: data.htmlContent || '',
      textContent: data.textContent,
      category: data.category || 'business',
      tags: data.tags || [],
      variables: data.variables || [],
      
      // Metadata
      createdBy: data.createdBy || 'system',
      createdAt: data.createdAt || Timestamp.now(),
      updatedAt: data.updatedAt || data.createdAt || Timestamp.now(),
      
      // Display
      previewImage: data.previewImage,
      isActive: data.isActive !== false,
      
      // Marketplace
      isPaid: data.isPaid || false,
      price: data.price || 0,
      downloadCount: data.downloadCount || 0,
      
      // Featured/ordering
      featuredOnDiscoverPage: data.featuredOnDiscoverPage || false,
      discoverPageOrder: data.discoverPageOrder,
      displayOrder: data.displayOrder,
      
      // Editor design
      designJson: data.designJson,
    };
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const systemTemplateService = new SystemTemplateService();
export default systemTemplateService;
